import { Link, type LinkProps } from "@tanstack/react-router";
import clsx from "clsx";
import { ChevronDownIcon, MenuIcon } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuSection,
  SubmenuTrigger,
  Text,
} from "react-aria-components";

import type { Viewer } from "@/auth";
import { useViewerId } from "@/auth/shared";
import { Popover } from "@/components/base/popover";
import { NewsletterModal } from "@/components/newsletter-form";
import { useSideNav } from "@/components/side-nav-context";
import { useRulebookUrl, useStoreUrl } from "@/hooks/settings";

type NavbarLinkItem = {
  kind: "item";
  key?: string;
  className?: string;
  topNavItemClassName?: string;
  subMenuClassName?: string;
  label: ReactNode;
  visible?: (viewer?: Viewer["id"] | null) => boolean;
} & (
  | { to: LinkProps["to"]; params?: LinkProps["params"]; href?: never }
  | { href: string; to?: never; params?: never }
);

type NavbarActionItem = {
  kind: "action";
  key: string;
  className?: string;
  topNavItemClassName?: string;
  subMenuClassName?: string;
  label: ReactNode;
  visible?: (viewer?: Viewer["id"] | null) => boolean;
};

type NavbarItem = NavbarLinkItem | NavbarActionItem;

type NavbarGroupItem = {
  kind: "group";
  key: string;
  className?: string;
  topNavItemClassName?: string;
  subMenuClassName?: string;
  label: ReactNode;
  visible?: (viewer?: Viewer["id"] | null) => boolean;
  children: NavbarItem[];
};

const links: (NavbarItem | NavbarGroupItem)[] = [
  {
    kind: "item",
    to: "/tournaments",
    label: "Tournaments",
  },
  {
    kind: "group",
    key: "juniors",
    label: "Juniors",
    children: [
      {
        kind: "item",
        to: "/juniors",
        label: "About",
      },
      {
        kind: "item",
        to: "/juniors/leaderboard/{-$gender}",
        params: { gender: "girls" },
        label: "Leaderboard",
      },
      {
        kind: "item",
        key: "parents-faq",
        to: "/faqs/{-$subject}",
        params: { subject: "parents" },
        label: "Parent's FAQ",
      },
    ],
  },
  {
    kind: "item",
    to: "/search",
    label: "Search",
  },
  {
    kind: "group",
    key: "info",
    label: "Info",
    topNavItemClassName: "hidden lg:flex",
    subMenuClassName: "inline-block lg:hidden",
    children: [
      {
        kind: "item",
        to: "/ratings",
        label: "Ratings",
      },
      {
        kind: "item",
        to: "/leaderboard/{-$gender}",
        params: { gender: "womens" },
        label: "Leaderboard",
      },
      {
        kind: "item",
        to: "/faqs/{-$subject}",
        label: "FAQs",
      },
      {
        kind: "item",
        to: "/gallery",
        label: "Photos",
      },
      {
        kind: "item",
        to: "/info/cedars",
        label: "Injury Prevention",
      },
      {
        kind: "item",
        to: "/prize-pool",
        label: "Prize Pool",
      },
      {
        kind: "action",
        key: "newsletter",
        label: "Newsletter",
      },
    ],
  },
  {
    kind: "item",
    visible: (viewer) => !Boolean(viewer),
    className: "bg-gray-500 hidden md:inline-block",
    subMenuClassName: "md:hidden",
    to: "/log-in",
    label: "Log In",
  },
  {
    kind: "item",
    visible: (viewer) => !Boolean(viewer),
    className: "bg-red-500 hidden md:inline-block",
    subMenuClassName: "md:hidden",
    to: "/sign-up",
    label: "Sign Up",
  },
  {
    kind: "item",
    visible: (viewer) => Boolean(viewer),
    className: "hidden md:inline-block",
    subMenuClassName: "md:hidden",
    to: "/account",
    label: "Account",
  },
];

const linkClassName =
  "uppercase text-navbar-foreground hover:bg-navbar-foreground hover:text-navbar-foreground-hover px-3 py-1 font-bold tracking-wide";

function DropdownItem({
  item,
  onAction,
}: {
  item: NavbarItem;
  onAction?: (key: string) => void;
}) {
  const { label, className, subMenuClassName, ...rest } = item;

  if (rest.kind === "action") {
    return (
      <MenuItem
        key={rest.key}
        className={clsx(
          "rounded-lg items-stretch justify-stretch",
          subMenuClassName,
        )}
        onAction={() => onAction?.(rest.key)}
      >
        <span
          className={clsx(
            linkClassName,
            "rounded-lg w-full inline-block cursor-pointer",
            className,
          )}
        >
          {label}
        </span>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      key={rest.key ?? rest.href ?? rest.to}
      className={clsx(
        "rounded-lg items-stretch justify-stretch",
        subMenuClassName,
      )}
    >
      {rest.href ? (
        <a
          href={rest.href}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(
            linkClassName,
            "rounded-lg w-full inline-block",
            className,
          )}
        >
          {label}
        </a>
      ) : (
        <Link
          to={rest.to}
          params={rest.params}
          className={clsx(
            linkClassName,
            "rounded-lg w-full inline-block",
            className,
          )}
        >
          {label}
        </Link>
      )}
    </MenuItem>
  );
}

function SideNavTrigger() {
  const { toggle } = useSideNav();
  return (
    <Button
      aria-label="Open navigation menu"
      onPress={toggle}
      className="text-navbar-foreground hover:bg-navbar-foreground hover:text-navbar-foreground-hover pressed:bg-navbar-foreground pressed:text-navbar-foreground-hover rounded-full p-2"
    >
      <MenuIcon className="h-5 w-5" />
    </Button>
  );
}

export function Navbar() {
  const viewerId = useViewerId();
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const storeUrl = useStoreUrl();
  const rulebookUrl = useRulebookUrl();

  const resolvedLinks: (NavbarItem | NavbarGroupItem)[] = useMemo(() => {
    return links.map((item) => {
      if (item.kind === "group" && item.key === "info") {
        const children = [...item.children];
        // Rulebook keeps its original slot before the Newsletter action.
        if (rulebookUrl) {
          const newsletterIdx = children.findIndex(
            (c) => c.kind === "action" && c.key === "newsletter",
          );
          const at = newsletterIdx === -1 ? children.length : newsletterIdx;
          children.splice(at, 0, {
            kind: "item",
            href: rulebookUrl,
            label: "Rulebook",
          });
        }
        if (storeUrl) {
          children.push({ kind: "item", href: storeUrl, label: "Store" });
        }
        return { ...item, children };
      }
      return item;
    });
  }, [storeUrl, rulebookUrl]);

  const visibleLinks = resolvedLinks.filter(({ visible = () => true }) =>
    visible(viewerId),
  );

  function handleAction(key: string) {
    if (key === "newsletter") setNewsletterOpen(true);
  }

  return (
    <nav className="bg-navbar-background flex w-full items-center gap-3 p-3">
      <Menu
        aria-label="Navigation Menu"
        className="flex w-full flex-1 flex-row items-center justify-between gap-3 lg:justify-center"
      >
        <MenuSection className="hidden flex-1 items-center justify-end gap-3 lg:flex">
          {visibleLinks
            .slice(0, 3)
            .map(({ label, className, topNavItemClassName, ...rest }) =>
              rest.kind === "item" ? (
                <MenuItem key={rest.to} className="rounded-full outline-none">
                  <Link
                    to={rest.to}
                    className={clsx(
                      linkClassName,
                      "rounded-full",
                      className,
                      topNavItemClassName,
                    )}
                  >
                    {label}
                  </Link>
                </MenuItem>
              ) : (
                <SubmenuTrigger key={rest.key}>
                  <MenuItem id={rest.key} className="rounded-full outline-none">
                    <Text
                      slot="label"
                      className={clsx(
                        linkClassName,
                        "rounded-full cursor-pointer flex flex-row",
                        className,
                        topNavItemClassName,
                      )}
                    >
                      {label}
                      <ChevronDownIcon className="ml-2" />
                    </Text>
                  </MenuItem>
                  <Popover placement="bottom">
                    <Menu className="bg-navbar-background border-navbar-border flex flex-col gap-2 rounded-lg border p-2">
                      {rest.children.map((child) => (
                        <DropdownItem
                          key={child.key ?? child.href ?? child.to}
                          item={child}
                          onAction={handleAction}
                        />
                      ))}
                    </Menu>
                  </Popover>
                </SubmenuTrigger>
              ),
            )}
        </MenuSection>
        <MenuItem className="flex rounded-full transition-colors outline-none hover:brightness-75 lg:justify-center">
          <Link to="/">
            <img alt="CBVA Logo" className="h-12" src="/logos/cbva.svg" />
          </Link>
        </MenuItem>
        <MenuSection className="hidden flex-1 justify-end gap-3 md:flex lg:justify-start">
          {visibleLinks
            .slice(3)
            .map(({ label, className, topNavItemClassName, ...rest }) =>
              rest.kind === "item" ? (
                <MenuItem key={rest.to} className="rounded-full outline-none">
                  <Link
                    to={rest.to}
                    className={clsx(
                      linkClassName,
                      "rounded-full",
                      className,
                      topNavItemClassName,
                    )}
                  >
                    {label}
                  </Link>
                </MenuItem>
              ) : (
                <SubmenuTrigger key={rest.key}>
                  <MenuItem id={rest.key} className="rounded-full outline-none">
                    <Text
                      slot="label"
                      className={clsx(
                        linkClassName,
                        "rounded-full flex flex-row cursor-pointer",
                        className,
                        topNavItemClassName,
                      )}
                    >
                      {label}
                      <ChevronDownIcon className="ml-2" />
                    </Text>
                  </MenuItem>
                  <Popover placement="bottom">
                    <Menu className="bg-navbar-background border-navbar-border flex flex-col gap-2 rounded-lg border p-2">
                      {rest.children.map((child) => (
                        <DropdownItem
                          key={child.key ?? child.href ?? child.to}
                          item={child}
                          onAction={handleAction}
                        />
                      ))}
                    </Menu>
                  </Popover>
                </SubmenuTrigger>
              ),
            )}
        </MenuSection>
      </Menu>
      <SideNavTrigger />
      <NewsletterModal
        isOpen={newsletterOpen}
        onOpenChange={setNewsletterOpen}
      />
    </nav>
  );
}
